import { UIElement } from "./ui_element.js";
import { MyTextAlign, UITextElement } from "./ui_text_element.js";
import { LocalizationManager } from "../../localization/localization_manager.js";
import { TextStyleScheme } from "./text_style_scheme.js";
import { ImagesHolder } from "../../image_loader/images_holder.js";
import { ImgID } from "../../image_loader/img_id.js";
import { CornerSizes, NineSliceDrawer } from "../../image_loader/nine_slicer/nine_slice_drawer.js";

const roundedRectPath = ( ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, radius: number ): void =>
{
  const r = Math.max( 0, Math.min( radius, Math.min( w, h ) / 2 ) );
  if ( typeof ctx.roundRect === "function" )
  {
    ctx.roundRect( x, y, w, h, r );
    return;
  }

  ctx.moveTo( x + r, y );
  ctx.lineTo( x + w - r, y );
  ctx.arcTo( x + w, y, x + w, y + r, r );
  ctx.lineTo( x + w, y + h - r );
  ctx.arcTo( x + w, y + h, x + w - r, y + h, r );
  ctx.lineTo( x + r, y + h );
  ctx.arcTo( x, y + h, x, y + h - r, r );
  ctx.lineTo( x, y + r );
  ctx.arcTo( x, y, x + r, y, r );
  ctx.closePath();
};

export interface UITextInputStyle
{
  font_size?: number;
  placeholder_font_size?: number;
  font_weight?: number;
  text_color?: string;
  placeholder_color?: string;
  background_color?: string;
  border_color?: string;
  border_color_focused?: string;
  border_color_error?: string;
  border_width?: number;
  border_radius?: number;
  padding?: number;
  padding_right?: number;
  text_style?: string;
  text_align?: MyTextAlign;
  outline_width?: number;
  outline_color?: string;
  outline_offset_y?: number;
  text_offset_y?: number;
  error_text_color?: string;
  error_background_color?: string;
  error_font_size?: number;
  background_nine_slice?: { img_id: ImgID; ns_key: string; corner_size?: CornerSizes };
}

export interface UITextInputOptions
{
  initial_value?: string;
  placeholder?: string;
  localization_key?: string;
  max_length?: number;
  is_multiline?: boolean;
  style?: UITextInputStyle;
}

export class UITextInput extends UIElement
{
  private static readonly IOS_MIN_INPUT_FONT_SIZE_PX = 16;

  private text_value: string;
  private placeholder: string;
  private localization_key: string;
  private font_size: number;
  private placeholder_font_size: number;
  private font_weight: number;
  private text_color: string;
  private placeholder_color: string;
  private background_color: string;
  private border_color: string;
  private border_color_focused: string;
  private border_color_error: string;
  private border_width: number;
  private border_radius: number;
  private padding: number;
  private padding_right: number = 0;
  private text_style: string = "sans-serif";
  private text_align: MyTextAlign = MyTextAlign.Left;
  private outline_width: number = 0;
  private outline_color: string = "#000000";
  private outline_offset_y: number = 0;
  private text_offset_y: number = 0;
  private error_text_color: string = "#FFFFFF";
  private error_background_color: string = "#E5484D";
  private error_font_size: number;
  private ns_bg_img_id?: ImgID;
  private ns_bg_ns_key?: string;
  private ns_bg_corner_size?: CornerSizes;
  public is_focused: boolean = false;

  private html_input!: HTMLInputElement | HTMLTextAreaElement;
  private is_html_active: boolean = false;
  private style_element?: HTMLStyleElement;

  private has_error: boolean = false;
  private error_message_key: string = "";
  private error_text_element?: UITextElement;
  private max_length: number = -1;
  private is_multiline: boolean = false;

  private onBlur?: () => void;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, options: UITextInputOptions = {} )
  {
    super( ctx, x, y, width, height );

    this.text_value = options.initial_value || "";
    this.placeholder = options.placeholder || "";
    this.localization_key = options.localization_key || "";
    this.max_length = options.max_length || -1;
    this.is_multiline = options.is_multiline || false;

    const style = options.style || {};
    this.font_size = style.font_size ?? LayoutScaler.textSizeFromDesign( 35 );
    this.placeholder_font_size = style.placeholder_font_size ?? this.font_size;
    this.font_weight = style.font_weight ?? 700;
    this.text_color = style.text_color ?? "#000000";
    this.placeholder_color = style.placeholder_color ?? "#256BB6";
    this.background_color = style.background_color ?? "#ffffff";
    this.border_color = style.border_color ?? "#4CAF50";
    this.border_color_focused = style.border_color_focused ?? "#00FFE5";
    this.border_color_error = style.border_color_error ?? "#E5484D";
    this.border_width = style.border_width ?? LayoutScaler.widthFromDesign(5);
    this.border_radius = style.border_radius ?? LayoutScaler.widthFromDesign(12);
    this.padding = style.padding ?? LayoutScaler.widthFromDesign(25);
    this.padding_right = style.padding_right ?? 0;
    this.text_style = style.text_style ?? "sans-serif";
    this.text_align = style.text_align ?? MyTextAlign.Left;
    this.outline_width = style.outline_width ?? 0;
    this.outline_color = style.outline_color ?? "#000000";
    this.outline_offset_y = style.outline_offset_y ?? 0;
    this.text_offset_y = style.text_offset_y ?? 0;
    this.error_text_color = style.error_text_color ?? "#FFFFFF";
    this.error_background_color = style.error_background_color ?? "#E5484D";
    this.error_font_size = style.error_font_size ?? LayoutScaler.textSizeFromDesign( 30 );

    if ( style.background_nine_slice )
    {
      this.ns_bg_img_id      = style.background_nine_slice.img_id;
      this.ns_bg_ns_key      = style.background_nine_slice.ns_key;
      this.ns_bg_corner_size = style.background_nine_slice.corner_size;
    }

    this.createHtmlInput();
  }

  private createHtmlInput(): void
  {
    if (this.html_input && this.html_input.parentNode) {
      this.html_input.parentNode.removeChild(this.html_input);
    }
    if (this.style_element && this.style_element.parentNode) {
      this.style_element.parentNode.removeChild(this.style_element);
    }

    this.html_input = this.is_multiline
      ? document.createElement( 'textarea' )
      : document.createElement( 'input' );

    if ( this.html_input instanceof HTMLInputElement )
    {
      this.html_input.type = 'text';
    }

    this.html_input.value = this.text_value;

    this.html_input.style.position = 'absolute';
    this.html_input.style.boxSizing = 'border-box';

    this.html_input.style.fontFamily = this.text_style;
    this.html_input.style.fontSize = `${this.getHtmlInputFontSizePx( this.font_size )}px`;
    this.html_input.style.fontWeight = this.font_weight.toString();
    this.html_input.style.color = this.text_color;

    this.html_input.style.backgroundColor = this.background_color;
    this.html_input.style.border = 'none';
    this.html_input.style.borderRadius = `${this.border_radius}px`;
    this.html_input.style.outline = 'none';

    this.html_input.style.padding = `${this.padding}px`;

    switch (this.text_align) {
      case MyTextAlign.Left:
        this.html_input.style.textAlign = 'left';
        break;
      case MyTextAlign.Center:
        this.html_input.style.textAlign = 'center';
        break;
      case MyTextAlign.Right:
        this.html_input.style.textAlign = 'right';
        break;
    }

    if ( this.html_input instanceof HTMLTextAreaElement ) {
      this.html_input.style.resize = 'none';
      this.html_input.style.overflow = 'auto';
      this.html_input.style.lineHeight = '1.0';
      this.html_input.style.verticalAlign = 'top';
    } else {
      this.html_input.style.lineHeight = 'normal';
      this.html_input.style.verticalAlign = 'middle';
    }

    this.html_input.tabIndex = 0;

    const style_id = `ui-text-input-placeholder-${Math.random().toString(36).substring(2, 9)}`;
    this.html_input.setAttribute('data-style-id', style_id);

    this.style_element = document.createElement('style');
    this.style_element.id = style_id;
    this.style_element.textContent = `
      [data-style-id="${style_id}"]::placeholder {
        color: ${this.placeholder_color} !important;
        opacity: 1;
      }
      [data-style-id="${style_id}"]::-webkit-input-placeholder {
        color: ${this.placeholder_color} !important;
        opacity: 1;
      }
      [data-style-id="${style_id}"]::-moz-placeholder {
        color: ${this.placeholder_color} !important;
        opacity: 1;
      }
      [data-style-id="${style_id}"]:-ms-input-placeholder {
        color: ${this.placeholder_color} !important;
        opacity: 1;
      }
    `;
    document.head.appendChild(this.style_element);

    document.body.appendChild( this.html_input );

    this.updatePlaceholder();
    this.setupEventHandlers();
    this.positionHtmlInputOverCanvas();
  }

  private setupEventHandlers(): void
  {
    this.html_input.addEventListener( 'input', ( e ) =>
    {
      if ( this.max_length > 0 && this.html_input.value.length > this.max_length )
      {
        this.html_input.value = this.html_input.value.substring( 0, this.max_length );
      }
      this.text_value = this.html_input.value;

      this.setShouldBeRendered();
      this.onValueChanged?.();
    } );

    this.html_input.addEventListener( 'keydown', ( e ) =>
    {
      const keyEvent = e as KeyboardEvent;
      if ( keyEvent.key === 'Enter' )
      {
        if ( this.is_multiline )
          return;
        else
        {
          keyEvent.preventDefault();
          this.blur();
          this.onEnterPressed?.();
        }
      }
      if ( keyEvent.key === 'Escape' )
      {
        keyEvent.preventDefault();
        this.blur();
        this.onEscapePressed?.();
      }
    } );

    this.html_input.addEventListener( 'paste', ( e ) =>
    {
      setTimeout( () =>
      {
        this.text_value = this.html_input.value;
        this.setShouldBeRendered();
        this.onValuePasted?.();
      }, 0 );
    } );

    this.html_input.addEventListener( 'focus', () =>
    {
      this.is_focused = true;
      this.updateBorderStyle();
    } );

    this.html_input.addEventListener( 'blur', () =>
    {
      this.is_focused = false;
      this.updateBorderStyle();

      this.onBlur?.();

      setTimeout( () =>
      {
        if ( this.is_html_active )
          this.blur();
      }, 100 );
    } );

    this.html_input.addEventListener( 'focusout', () =>
    {
      setTimeout( () =>
      {
        if ( this.is_html_active && this.isMobileDevice() )
          this.blur();
      }, 100 );
    } );
  }

  public setOnBlur( onBlur: () => void ): void
  {
    this.onBlur = onBlur;
  }

  public focus(): void
  {
    if ( this.is_focused )
      return;

    this.is_focused = true;
    this.is_html_active = true;

    this.updateBorderStyle();
    this.html_input.focus();
    this.setShouldBeRendered();
  }

  public blur(): void
  {
    this.is_focused = false;
    this.is_html_active = false;

    this.updateBorderStyle();
    this.html_input.blur();
    this.setShouldBeRendered();
  }


  public setValue( value: string ): void
  {
    this.text_value = value;
    this.html_input.value = value;
    this.setShouldBeRendered();
  }

  public getValue(): string
  {
    return this.text_value;
  }

  public setError( error_message_key: string ): void
  {
    this.has_error = true;
    this.error_message_key = error_message_key;
    this.createErrorTextElement();

    this.updateBorderStyle();

    this.setShouldBeRendered();
  }

  public clearError(): void
  {
    this.has_error = false;
    this.error_message_key = "";
    if ( this.error_text_element )
    {
      this.removeChild( this.error_text_element );
      this.error_text_element = undefined;
    }

    this.updateBorderStyle();
    this.setShouldBeRendered();
  }

  public hasError(): boolean
  {
    return this.has_error;
  }

  private createErrorTextElement(): void
  {
    if ( this.error_text_element )
    {
      this.removeChild( this.error_text_element );
    }

    const error_height = LayoutScaler.heightFromDesign( 41 );
    const error_y = this.rect.getH();

    this.error_text_element = new UITextElement(
      this.ctx,
      0,
      error_y,
      this.rect.getW(),
      error_height,
      {
        localization_key: this.error_message_key,
        text_color: this.error_text_color,
        text_align: MyTextAlign.Center,
        font_weight: 700,
        font_size: this.error_font_size,
        text_style: TextStyleScheme.rubik,
      }
    );

    this.addChild( this.error_text_element );
  }

  public setMaxLength( max_length: number ): void
  {
    this.max_length = max_length;
  }

  public getMaxLength(): number
  {
    return this.max_length;
  }

  public setMultiline( is_multiline: boolean ): void
  {
    if (this.is_multiline !== is_multiline) {
      this.is_multiline = is_multiline;
      this.createHtmlInput();
    }
  }

  public getRequiredHeight(): number
  {
    if ( !this.is_multiline || !this.text_value )
    {
      return this.rect.getH();
    }

    const font_size = this.font_size;
    const line_height = font_size * 1.0;
    const text_padding = LayoutScaler.widthFromDesign(25);
    const available_width = this.rect.getW() - text_padding * 2;

    this.ctx.font = `${this.font_weight} ${font_size}px ${this.text_style}`;

    const lines = this.wrapTextToLines( this.text_value, available_width );
    const required_text_height = lines.length * line_height;
    const total_height = required_text_height + text_padding * 2;

    const min_height = LayoutScaler.heightFromDesign( 220 );
    return Math.max( min_height, total_height );
  }

  public updateInputSize(): void
  {
    if ( this.is_multiline )
    {
      const required_height = this.getRequiredHeight();
      const current_height = this.rect.getH();

      if ( Math.abs( required_height - current_height ) > 5 )
      {
        this.rect.resize( this.rect.getW(), required_height );

        if ( this.has_error )
        {
          this.createErrorTextElement();
        }

        this.setShouldBeRendered();
      }
    }
  }

  public static validateEmail( email: string ): boolean
  {
    const email_regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return email_regex.test( email );
  }

  private wrapTextToLines( text: string, max_width: number ): string[]
  {
    if ( !text )
      return [''];

    const lines: string[] = [];
    const paragraphs = text.split('\n');

    for ( const paragraph of paragraphs )
    {
      if ( paragraph.length === 0 )
      {
        lines.push('');
        continue;
      }

      const words = paragraph.split(' ');
      let current_line = '';

      for ( const word of words )
      {
        const test_line = current_line ? current_line + ' ' + word : word;
        const test_width = this.ctx.measureText( test_line ).width;

        if ( test_width <= max_width )
          current_line = test_line;
        else
        {
          if ( current_line )
          {
            lines.push( current_line );
            current_line = word;
          }
          else
            lines.push( word );
        }
      }

      if ( current_line )
        lines.push( current_line );
    }

    return lines;
  }



  public onEnterPressed?: () => void;
  public onEscapePressed?: () => void;
  public onValuePasted?: () => void;
  public onValueChanged?: () => void;

  private isMobileDevice(): boolean
  {
    const userAgent = navigator.userAgent;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test( userAgent );
  }

  private isIOSDevice(): boolean
  {
    const ua = navigator.userAgent;
    const ios_user_agent = /iPhone|iPad|iPod/i.test( ua );
    const ipad_desktop_mode = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
    return ios_user_agent || ipad_desktop_mode;
  }

  private getHtmlInputFontSizePx( requested_size: number ): number
  {
    if ( this.isIOSDevice() )
    {
      return Math.max( requested_size, UITextInput.IOS_MIN_INPUT_FONT_SIZE_PX );
    }
    return requested_size;
  }

  private updatePlaceholder(): void
  {
    if (this.localization_key) {
      const placeholder_text = LocalizationManager.getInstance().getText(
        LocalizationManager.getCurrentLanguage(),
        this.localization_key
      );
      this.html_input.placeholder = placeholder_text || this.placeholder;
    } else {
      this.html_input.placeholder = this.placeholder;
    }
  }

  private updateBorderStyle(): void
  {
    this.html_input.style.backgroundColor = this.background_color;
    this.html_input.style.borderRadius = `${this.border_radius}px`;

    if (this.has_error) {
      this.html_input.style.border = `${this.border_width}px solid ${this.border_color_error}`;
    } else if (this.is_focused) {
      this.html_input.style.border = `${this.border_width}px solid ${this.border_color_focused}`;
    } else {
      this.html_input.style.border = 'none';
    }
  }

  private positionHtmlInputOverCanvas(): void
  {
    const canvas      = this.ctx.canvas;
    const canvas_rect = canvas.getBoundingClientRect();

    const layout = this.getAbsoluteLayoutForHtml();
    const sx     = layout.scale_x;
    const sy     = layout.scale_y;

    const w = ( this.rect.getW() - this.padding_right ) * sx;
    const h = this.rect.getH() * sy;

    this.html_input.style.left      = `${canvas_rect.left + window.scrollX + layout.x}px`;
    this.html_input.style.top       = `${canvas_rect.top  + window.scrollY + layout.y}px`;
    this.html_input.style.width     = `${w}px`;
    this.html_input.style.height    = `${h}px`;
    this.html_input.style.fontSize  = `${this.getHtmlInputFontSizePx( this.font_size * sx )}px`;
    this.html_input.style.transform = 'none';

    const actual_opacity = this.getActualOpacity();
    this.html_input.style.zIndex        = '130';
    this.html_input.style.opacity       = ( this.is_html_active && actual_opacity > 0.01 ) ? String( actual_opacity ) : '0';
    this.html_input.style.pointerEvents = ( this.is_html_active && actual_opacity > 0.01 ) ? 'auto' : 'none';

    this.updateBorderStyle();
  }

  public updateBasic( now: number ): void
  {
    super.updateBasic( now );
    this.positionHtmlInputOverCanvas();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();

    const x = -this.rect.getW2();
    const y = -this.rect.getH2();
    const w = this.rect.getW();
    const h = this.rect.getH();

    if ( this.has_error )
    {
      const error_height = LayoutScaler.heightFromDesign( 41 );

      this.ctx.fillStyle = this.error_background_color;
      this.ctx.beginPath();
      roundedRectPath( this.ctx, x, y, w, h + error_height, this.border_radius );
      this.ctx.fill();
    }

    if ( !this.ns_bg_ns_key )
    {
      this.ctx.fillStyle = this.background_color;
      this.ctx.beginPath();
      roundedRectPath( this.ctx, x, y, w, h, this.border_radius );
      this.ctx.fill();
    }
    else
    {
      this.drawNineSliceBackground();
    }

    if ( this.has_error )
    {
      this.ctx.strokeStyle = this.border_color_error;
      this.ctx.lineWidth = this.border_width;
      this.ctx.beginPath();
      roundedRectPath( this.ctx, x, y, w, h, this.border_radius );
      this.ctx.stroke();
    }

    if ( !this.is_html_active )
      this.drawTextOnCanvas();

    this.finishRender();
  }

  private drawTextOnCanvas(): void
  {
    const is_empty = this.text_value.length === 0;

    if ( is_empty )
    {
      const placeholder = this.html_input.placeholder;
      if ( !placeholder ) return;

      const w2 = this.rect.getW() / 2;

      this.ctx.save();
      this.ctx.font         = `${this.font_weight} ${this.placeholder_font_size}px ${this.text_style}`;
      this.ctx.fillStyle    = this.placeholder_color;
      this.ctx.textBaseline = 'middle';
      this.ctx.textAlign    = 'left';
      this.ctx.fillText( placeholder, -w2 + this.padding, this.text_offset_y );
      this.ctx.restore();
      return;
    }

    const text = this.text_value;

    const w  = this.rect.getW();
    const w2 = w / 2;
    const h2 = this.rect.getH() / 2;

    this.ctx.save();
    this.ctx.font          = `${this.font_weight} ${this.font_size}px ${this.text_style}`;
    this.ctx.fillStyle     = this.text_color;
    this.ctx.textBaseline  = 'middle';

    let text_x: number;
    if ( this.text_align === MyTextAlign.Center )
    {
      this.ctx.textAlign = 'center';
      text_x = 0;
    }
    else if ( this.text_align === MyTextAlign.Right )
    {
      this.ctx.textAlign = 'right';
      text_x = w2 - this.padding;
    }
    else
    {
      this.ctx.textAlign = 'left';
      text_x = -w2 + this.padding;
    }

    if ( this.outline_width > 0 )
    {
      this.ctx.strokeStyle = this.outline_color;
      this.ctx.lineWidth   = this.outline_width;
      this.ctx.strokeText( text, text_x, this.text_offset_y + this.outline_offset_y );
    }

    this.ctx.fillText( text, text_x, this.text_offset_y );
    this.ctx.restore();
  }

  private drawNineSliceBackground(): void
  {
    if ( !this.ns_bg_img_id || !this.ns_bg_ns_key ) return;

    NineSliceDrawer.drawNineSliceImage(
      this.ctx,
      ImagesHolder.getImage( this.ns_bg_img_id ),
      this.ns_bg_ns_key,
      -this.rect.getW2(),
      -this.rect.getH2(),
      this.rect.getW(),
      this.rect.getH(),
      this.ns_bg_corner_size
    );
  }

  public cleanup(): void
  {
    if (this.html_input && this.html_input.parentNode) {
      this.html_input.parentNode.removeChild(this.html_input);
    }

    if (this.style_element && this.style_element.parentNode) {
      this.style_element.parentNode.removeChild(this.style_element);
    }
  }

  public applyStyles( style: UITextInputStyle ): void
  {
    let needs_recreate = false;

    if ( style.font_size !== undefined ) this.font_size = style.font_size;
    if ( style.placeholder_font_size !== undefined ) this.placeholder_font_size = style.placeholder_font_size;
    if ( style.font_weight !== undefined ) this.font_weight = style.font_weight;
    if ( style.text_color !== undefined ) this.text_color = style.text_color;
    if ( style.background_color !== undefined ) this.background_color = style.background_color;
    if ( style.border_color !== undefined ) this.border_color = style.border_color;
    if ( style.border_color_focused !== undefined ) this.border_color_focused = style.border_color_focused;
    if ( style.border_color_error !== undefined ) this.border_color_error = style.border_color_error;
    if ( style.border_width !== undefined ) this.border_width = style.border_width;
    if ( style.border_radius !== undefined ) this.border_radius = style.border_radius;
    if ( style.padding !== undefined ) this.padding = style.padding;
    if ( style.text_style !== undefined ) this.text_style = style.text_style;
    if ( style.text_align !== undefined ) this.text_align = style.text_align;
    if ( style.outline_width !== undefined ) this.outline_width = style.outline_width;
    if ( style.outline_color !== undefined ) this.outline_color = style.outline_color;
    if ( style.outline_offset_y !== undefined ) this.outline_offset_y = style.outline_offset_y;
    if ( style.text_offset_y !== undefined ) this.text_offset_y = style.text_offset_y;
    if ( style.error_text_color !== undefined ) this.error_text_color = style.error_text_color;
    if ( style.error_background_color !== undefined ) this.error_background_color = style.error_background_color;
    if ( style.error_font_size !== undefined ) this.error_font_size = style.error_font_size;

    if ( style.placeholder_color !== undefined && style.placeholder_color !== this.placeholder_color )
    {
      this.placeholder_color = style.placeholder_color;
      needs_recreate = true;
    }

    if ( needs_recreate )
    {
      this.createHtmlInput();
    }
    else
    {
      if ( style.font_size !== undefined ) this.html_input.style.fontSize = `${this.getHtmlInputFontSizePx( this.font_size )}px`;
      if ( style.font_weight !== undefined ) this.html_input.style.fontWeight = this.font_weight.toString();
      if ( style.text_color !== undefined ) this.html_input.style.color = this.text_color;
      if ( style.background_color !== undefined ) this.html_input.style.backgroundColor = this.background_color;
      if ( style.border_radius !== undefined ) this.html_input.style.borderRadius = `${this.border_radius}px`;
      if ( style.padding !== undefined ) this.html_input.style.padding = `${this.padding}px`;
      if ( style.text_style !== undefined ) this.html_input.style.fontFamily = this.text_style;

      if ( style.text_align !== undefined )
      {
        switch (this.text_align) {
          case MyTextAlign.Left: this.html_input.style.textAlign = 'left'; break;
          case MyTextAlign.Center: this.html_input.style.textAlign = 'center'; break;
          case MyTextAlign.Right: this.html_input.style.textAlign = 'right'; break;
        }
      }

      this.updateBorderStyle();
    }

    if ( this.has_error && (style.error_text_color !== undefined || style.error_font_size !== undefined) )
    {
      this.createErrorTextElement();
    }

    this.setShouldBeRendered();
  }

  public setBackgroundColor( color: string ): void
  {
    this.background_color = color;
    this.html_input.style.backgroundColor = color;
    this.setShouldBeRendered();
  }

  public setTextColor( color: string ): void
  {
    this.text_color = color;
    this.html_input.style.color = color;
    this.setShouldBeRendered();
  }

  public setPlaceholderColor( color: string ): void
  {
    this.placeholder_color = color;
    this.createHtmlInput();
    this.setShouldBeRendered();
  }

  public setBorderColors( normal?: string, focused?: string, error?: string ): void
  {
    if ( normal !== undefined ) this.border_color = normal;
    if ( focused !== undefined ) this.border_color_focused = focused;
    if ( error !== undefined ) this.border_color_error = error;
    this.updateBorderStyle();
    this.setShouldBeRendered();
  }

  public setFontSize( size: number ): void
  {
    this.font_size = size;
    this.html_input.style.fontSize = `${this.getHtmlInputFontSizePx( size )}px`;
    this.setShouldBeRendered();
  }

  public setBorderRadius( radius: number ): void
  {
    this.border_radius = radius;
    this.html_input.style.borderRadius = `${radius}px`;
    this.setShouldBeRendered();
  }

  public setPadding( padding: number ): void
  {
    this.padding = padding;
    this.html_input.style.padding = `${padding}px`;
    this.setShouldBeRendered();
  }

  public setBorderWidth( width: number ): void
  {
    this.border_width = width;
    this.updateBorderStyle();
    this.setShouldBeRendered();
  }

  public name(): string
  {
    return "UITextInput";
  }
}
